
import React from "react";
import Layout from "@/components/layout/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { SectionHeader } from "@/components/ui/section-header";

export default function DesignSystemGuide() {
  return (
    <Layout>
      <div className="container py-12">
        <SectionHeader 
          title="Design System Guide" 
          subtitle="A comprehensive documentation of design patterns and components used in the Grandpal application"
          className="mb-12"
        />

        <Tabs defaultValue="colors">
          <TabsList className="mb-8">
            <TabsTrigger value="colors">Colors</TabsTrigger>
            <TabsTrigger value="typography">Typography</TabsTrigger>
            <TabsTrigger value="spacing">Spacing</TabsTrigger>
            <TabsTrigger value="components">Components</TabsTrigger>
            <TabsTrigger value="workflow">Workflow</TabsTrigger>
            <TabsTrigger value="responsive">Responsive Testing</TabsTrigger>
          </TabsList>

          <TabsContent value="colors" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Color Palette</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <ColorSwatch name="Primary (Green)" color="#4c7f65" textColor="white" />
                  <ColorSwatch name="Primary Hover" color="#3f6a52" textColor="white" />
                  <ColorSwatch name="Green Light" color="#a8d5ba" />
                  <ColorSwatch name="Green Lighter" color="#e9f4ed" />
                  
                  <ColorSwatch name="Beige" color="#F5F1EA" />
                  <ColorSwatch name="Beige Dark" color="#E5DBC7" />
                  
                  <ColorSwatch name="Gray" color="#767676" textColor="white" />
                  <ColorSwatch name="Gray Light" color="#F8F8F8" />
                  
                  <ColorSwatch name="Background" color="hsl(40 30% 96%)" />
                  <ColorSwatch name="Foreground" color="hsl(120 12% 22%)" textColor="white" />
                  
                  <ColorSwatch name="Accent" color="hsl(20 100% 68%)" />
                  <ColorSwatch name="Muted" color="hsl(30 20% 94%)" />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col items-start">
                <p className="text-sm text-muted-foreground">
                  These colors are defined in <code>tailwind.config.ts</code> under the theme.extend.colors.grandpal namespace.
                </p>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Semantic Colors</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <ColorSwatch name="Primary" color="hsl(154 29% 25%)" textColor="white" />
                  <ColorSwatch name="Secondary" color="hsl(30 38% 84%)" />
                  <ColorSwatch name="Destructive" color="hsl(0 84.2% 60.2%)" textColor="white" />
                  <ColorSwatch name="Border" color="hsl(30 20% 88%)" />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col items-start">
                <p className="text-sm text-muted-foreground">
                  These semantic colors are defined in <code>index.css</code> as CSS variables.
                </p>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="typography" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Typography System</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Font Families</h3>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-md">
                      <div className="text-sm text-muted-foreground mb-2">Primary Font (DM Sans)</div>
                      <p className="text-2xl">The quick brown fox jumps over the lazy dog.</p>
                      <div className="text-sm text-muted-foreground mt-2">Used for body text and UI elements</div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <div className="text-sm text-muted-foreground mb-2">Heading Font (Lora)</div>
                      <p className="text-2xl font-lora">The quick brown fox jumps over the lazy dog.</p>
                      <div className="text-sm text-muted-foreground mt-2">Used for headings and titles</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4">Heading Styles</h3>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-md">
                      <h1 className="hero-text">Hero Text (h1)</h1>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-4xl md:text-5xl lg:text-6xl font-semibold
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <h2 className="section-title">Section Title (h2)</h2>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-3xl md:text-4xl font-semibold
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <h3 className="text-2xl font-medium">Heading 3</h3>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-2xl font-medium
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <h4 className="text-xl font-medium">Heading 4</h4>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-xl font-medium
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-4">Body Text</h3>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-md">
                      <p className="text-base">Base text (16px)</p>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-base
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <p className="text-sm">Small text (14px)</p>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-sm
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-md">
                      <p className="text-xs">Extra small text (12px)</p>
                      <div className="text-sm text-muted-foreground mt-2">
                        text-xs
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="spacing" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Spacing System</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p>
                  We use Tailwind's default spacing scale, which is based on a 4px grid (0.25rem increments).
                  Here are the most commonly used spacing values in our application:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Margin & Padding</h3>
                    <SpacingExample size={1} label="0.25rem (4px)" />
                    <SpacingExample size={2} label="0.5rem (8px)" />
                    <SpacingExample size={4} label="1rem (16px)" />
                    <SpacingExample size={6} label="1.5rem (24px)" />
                    <SpacingExample size={8} label="2rem (32px)" />
                    <SpacingExample size={12} label="3rem (48px)" />
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Gap Spacing</h3>
                    <div className="border p-4 rounded-md">
                      <div className="flex flex-col gap-2 mb-4">
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                      </div>
                      <div className="text-sm">gap-2: 0.5rem (8px)</div>
                    </div>
                    
                    <div className="border p-4 rounded-md">
                      <div className="flex flex-col gap-4 mb-4">
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                      </div>
                      <div className="text-sm">gap-4: 1rem (16px)</div>
                    </div>
                    
                    <div className="border p-4 rounded-md">
                      <div className="flex flex-col gap-8 mb-4">
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                        <div className="h-4 w-full bg-grandpal-green rounded-md"></div>
                      </div>
                      <div className="text-sm">gap-8: 2rem (32px)</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h4 className="font-medium mb-2">Spacing Best Practices</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Use consistent spacing within components (typically p-4 for card padding)</li>
                    <li>Use gap classes for flexbox and grid layouts instead of margins where possible</li>
                    <li>For vertical spacing between sections, use my-8, my-12, or my-16</li>
                    <li>For mobile views, consider reducing spacing (e.g., p-4 md:p-6)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="components" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Button Variants</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Default Variants</h3>
                    <div className="flex flex-wrap gap-4">
                      <Button variant="default">Default</Button>
                      <Button variant="secondary">Secondary</Button>
                      <Button variant="destructive">Destructive</Button>
                      <Button variant="outline">Outline</Button>
                      <Button variant="ghost">Ghost</Button>
                      <Button variant="link">Link</Button>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Button Sizes</h3>
                    <div className="flex flex-wrap items-center gap-4">
                      <Button size="lg">Large</Button>
                      <Button size="default">Default</Button>
                      <Button size="sm">Small</Button>
                      <Button size="icon"><span className="i-lucide-heart h-4 w-4" /></Button>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8">
                  <h3 className="font-medium text-lg mb-4">Custom Button Styles</h3>
                  <div className="flex flex-wrap gap-4">
                    <Button className="bg-grandpal-green-dark">Green Button</Button>
                    <Button className="bg-grandpal-beige text-primary hover:bg-grandpal-beige-dark">Beige Button</Button>
                    <Button variant="outline" className="border-grandpal-green-dark text-grandpal-green-dark hover:bg-grandpal-green-lighter">
                      Outline Green
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Form Elements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Text Inputs</h3>
                    <div className="space-y-4">
                      <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="default-input">Default Input</Label>
                        <Input id="default-input" placeholder="Enter text..." />
                      </div>
                      
                      <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="disabled-input">Disabled Input</Label>
                        <Input id="disabled-input" placeholder="Disabled input" disabled />
                      </div>
                      
                      <div className="grid w-full items-center gap-1.5">
                        <Label htmlFor="textarea-example">Textarea</Label>
                        <Textarea id="textarea-example" placeholder="Type your message here..." />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="font-medium text-lg">Selection Controls</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="terms" />
                        <Label htmlFor="terms">Accept terms and conditions</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Switch id="airplane-mode" />
                        <Label htmlFor="airplane-mode">Airplane Mode</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cards & Containers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-medium text-lg mb-4">Standard Card</h3>
                    <Card>
                      <CardHeader>
                        <CardTitle>Card Title</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p>This is the standard card component with header, content, and footer.</p>
                      </CardContent>
                      <CardFooter>
                        <Button>Action Button</Button>
                      </CardFooter>
                    </Card>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-lg mb-4">Custom Card Styles</h3>
                    <Card className="bg-grandpal-beige border-none shadow-sm">
                      <CardHeader>
                        <CardTitle>Custom Card</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p>This card uses a custom background color.</p>
                      </CardContent>
                      <CardFooter>
                        <Button variant="outline">Learn More</Button>
                      </CardFooter>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Other UI Components</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="space-y-2">
                    <h3 className="font-medium text-lg">Badges</h3>
                    <div className="flex flex-wrap gap-2">
                      <Badge>Default</Badge>
                      <Badge variant="secondary">Secondary</Badge>
                      <Badge variant="outline">Outline</Badge>
                      <Badge variant="destructive">Destructive</Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-lg">Avatars</h3>
                    <div className="flex flex-wrap gap-2">
                      <Avatar>
                        <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
                        <AvatarFallback>CN</AvatarFallback>
                      </Avatar>
                      <Avatar>
                        <AvatarFallback className="bg-grandpal-green text-white">GP</AvatarFallback>
                      </Avatar>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-medium text-lg">Alerts</h3>
                    <Alert>
                      <AlertTitle>Information</AlertTitle>
                      <AlertDescription>
                        This is an informational alert for the user.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="workflow" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Component Creation Workflow</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-grandpal-beige p-6 rounded-lg">
                  <h3 className="text-xl font-medium mb-4">Step 1: Design</h3>
                  <div className="space-y-2">
                    <p><strong>Key Activities:</strong></p>
                    <ul className="list-disc ml-6 space-y-2">
                      <li>Identify the component's purpose and requirements</li>
                      <li>Check the design system for existing patterns</li>
                      <li>Design the component using Tailwind classes</li>
                      <li>Consider all states: default, hover, focus, disabled, etc.</li>
                      <li>Document any design decisions</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-grandpal-green-lighter p-6 rounded-lg">
                  <h3 className="text-xl font-medium mb-4">Step 2: Development</h3>
                  <div className="space-y-2">
                    <p><strong>Key Activities:</strong></p>
                    <ul className="list-disc ml-6 space-y-2">
                      <li>Create a new file in the appropriate directory</li>
                      <li>Implement the component with TypeScript and Tailwind CSS</li>
                      <li>Add props with proper TypeScript interfaces</li>
                      <li>Ensure responsiveness across all screen sizes</li>
                      <li>Add documentation comments</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-grandpal-sage-100 p-6 rounded-lg">
                  <h3 className="text-xl font-medium mb-4">Step 3: Testing</h3>
                  <div className="space-y-2">
                    <p><strong>Key Activities:</strong></p>
                    <ul className="list-disc ml-6 space-y-2">
                      <li>Test the component in isolation</li>
                      <li>Test the component in context with other components</li>
                      <li>Verify responsiveness across all breakpoints</li>
                      <li>Test accessibility features</li>
                      <li>Update documentation with usage examples</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-muted p-6 rounded-lg">
                  <h3 className="text-xl font-medium mb-4">Documentation Standards</h3>
                  <div className="space-y-2">
                    <p><strong>Each component should include:</strong></p>
                    <ul className="list-disc ml-6 space-y-2">
                      <li>Clear TypeScript interfaces</li>
                      <li>JSDoc comments explaining the component purpose</li>
                      <li>Props documentation including default values</li>
                      <li>Usage examples</li>
                      <li>Any special considerations or limitations</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="responsive" className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Responsive Testing Process</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-4 border-b">
                    <h3 className="font-medium">Breakpoints Reference</h3>
                  </div>
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Breakpoint</th>
                            <th className="text-left p-2">Class Prefix</th>
                            <th className="text-left p-2">Width</th>
                            <th className="text-left p-2">Device Examples</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-2">Default</td>
                            <td className="p-2"><code>-</code></td>
                            <td className="p-2">0px+</td>
                            <td className="p-2">Small mobile devices</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-2">Small</td>
                            <td className="p-2"><code>sm:</code></td>
                            <td className="p-2">640px+</td>
                            <td className="p-2">Large mobile devices</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-2">Medium</td>
                            <td className="p-2"><code>md:</code></td>
                            <td className="p-2">768px+</td>
                            <td className="p-2">Tablets</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-2">Large</td>
                            <td className="p-2"><code>lg:</code></td>
                            <td className="p-2">1024px+</td>
                            <td className="p-2">Laptops/Desktops</td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-2">X-Large</td>
                            <td className="p-2"><code>xl:</code></td>
                            <td className="p-2">1280px+</td>
                            <td className="p-2">Large desktops</td>
                          </tr>
                          <tr>
                            <td className="p-2">2X-Large</td>
                            <td className="p-2"><code>2xl:</code></td>
                            <td className="p-2">1536px+</td>
                            <td className="p-2">Very large screens</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <h3 className="text-xl font-medium">Testing Checklist</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border p-4 rounded-md">
                      <h4 className="font-medium mb-2">Mobile Testing</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Test at 320px, 375px, and 428px widths</li>
                        <li>Verify readability of all text</li>
                        <li>Check touch target sizes (min 44x44px)</li>
                        <li>Ensure no horizontal scrollbars</li>
                        <li>Test both portrait and landscape</li>
                      </ul>
                    </div>
                    
                    <div className="border p-4 rounded-md">
                      <h4 className="font-medium mb-2">Tablet Testing</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Test at 768px and 820px widths</li>
                        <li>Verify layout transitions from mobile</li>
                        <li>Check for appropriate content density</li>
                        <li>Test both portrait and landscape</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border p-4 rounded-md">
                      <h4 className="font-medium mb-2">Desktop Testing</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Test at 1024px, 1280px, and 1536px widths</li>
                        <li>Verify optimal content layout and density</li>
                        <li>Check for appropriate white space usage</li>
                        <li>Test hover states and desktop interactions</li>
                      </ul>
                    </div>
                    
                    <div className="border p-4 rounded-md">
                      <h4 className="font-medium mb-2">Accessibility Testing</h4>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>Test keyboard navigation</li>
                        <li>Check color contrast ratios</li>
                        <li>Test with screen readers</li>
                        <li>Verify focus states are visible</li>
                        <li>Test zoomed views (up to 200%)</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-muted p-4 rounded-md">
                  <h4 className="font-medium mb-2">Tools for Responsive Testing</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Browser DevTools (Chrome, Firefox)</li>
                    <li>BrowserStack for real device testing</li>
                    <li>Chrome's mobile emulation mode</li>
                    <li>Firefox Responsive Design Mode</li>
                    <li>Web Page Test for performance across devices</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

// Helper components for the design system
interface ColorSwatchProps {
  name: string;
  color: string;
  textColor?: "white" | "black";
}

function ColorSwatch({ name, color, textColor = "black" }: ColorSwatchProps) {
  return (
    <div className="flex flex-col">
      <div 
        className="h-20 rounded-md mb-2 border" 
        style={{ backgroundColor: color }}
      ></div>
      <div className="text-sm font-medium">{name}</div>
      <div className="text-xs text-muted-foreground">{color}</div>
    </div>
  );
}

interface SpacingExampleProps {
  size: number;
  label: string;
}

function SpacingExample({ size, label }: SpacingExampleProps) {
  return (
    <div className="border p-4 rounded-md">
      <div className="flex items-center mb-2">
        <div className={`bg-grandpal-green-dark h-4`} style={{ width: `${size * 0.25}rem` }}></div>
        <div className="ml-4">{`p-${size}`}</div>
      </div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}
